import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCurrency } from '../../context/CurrencyContext';
import { normalizeCurrencyCode } from '../../lib/currency';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Button from '../../components/Button/Button';
import { createStripeCheckoutSession } from '../../lib/stripeFunctions';
import { getFlightOffer, summarizeFlightOffer } from '../../lib/duffelFlightsApi';
import './FlightPayment.css';

const PAY_STORAGE_PREFIX = 'tourasco_pay_';

const FlightPayment = ({ onGoHome, onNavigate }) => {
  const { formatFrom, selectedCurrency } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const { resumeKey, offerId } = location.state || {};

  const [offer, setOffer] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [payError, setPayError] = useState('');
  const [payLoading, setPayLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!resumeKey || !offerId) {
        setLoadError('Invalid payment session. Start again from flight search.');
        return;
      }
      const raw = sessionStorage.getItem(PAY_STORAGE_PREFIX + resumeKey);
      if (!raw) {
        setLoadError('Session expired. Go back and enter traveler details again.');
        return;
      }
      try {
        const parsed = JSON.parse(raw);
        if (parsed.type !== 'flight' || parsed.offerId !== offerId) {
          throw new Error('Session does not match this flight.');
        }
      } catch {
        setLoadError('Could not read booking session.');
        return;
      }

      try {
        const res = await getFlightOffer(offerId);
        const o = res?.data;
        if (cancelled) return;
        if (!o) {
          setLoadError('This offer is no longer available. Search again.');
          return;
        }
        setOffer(o);
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || 'Failed to load offer.');
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [resumeKey, offerId]);

  const summary = useMemo(() => summarizeFlightOffer(offer), [offer]);

  const routeTitle = useMemo(() => {
    const rows = summary?.slices || [];
    if (!rows.length) return 'Flight booking';
    return rows.map((r) => `${r.origin}–${r.dest}`).join(', ');
  }, [summary]);

  const handlePay = async () => {
    if (!offer || !resumeKey || !offerId) return;
    setPayError('');
    setPayLoading(true);
    try {
      const amount = Number(offer.total_amount);
      const currency = String(offer.total_currency || 'gbp').toLowerCase();
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error('Invalid price for checkout.');
      }

      const url = await createStripeCheckoutSession({
        amount,
        currency,
        hotelName: `Flight · ${routeTitle}`,
        propertyId: offerId,
        resumeKey,
        cancelPath: `/flights/book?offer=${encodeURIComponent(offerId)}`,
        successPath: '/flights/booking-confirmation',
      });

      window.location.href = url;
    } catch (e) {
      setPayError(e?.message || 'Could not start payment.');
    } finally {
      setPayLoading(false);
    }
  };

  const fmtShortTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  return (
    <div className="flight-payment-page">
      <Header onLogoClick={onGoHome} onNavigate={onNavigate} />
      <main className="flight-payment-main">
        <div className="flight-payment-container">
          <nav className="flight-payment-nav">
            <Link className="flight-payment-nav__link" to={`/flights/book?offer=${encodeURIComponent(offerId || '')}`}>
              ← Edit traveler details
            </Link>
          </nav>

          <header className="flight-payment-head">
            <h1 className="flight-payment-title">Payment</h1>
            <p className="flight-payment-lead">
              You’ll complete checkout securely with Stripe. After payment we confirm your flights with the airline (Duffel).
            </p>
          </header>

          {loadError ? <div className="flight-payment-error">{loadError}</div> : null}

          {!loadError && offer && summary ? (
            <>
              <section className="flight-payment-card">
                <div className="flight-payment-card__accent" aria-hidden />
                <div className="flight-payment-card__inner">
                  <h2 className="flight-payment-card__title">You’re paying for</h2>
                  <ul className="flight-payment-routes">
                    {summary.slices?.map((row, idx) => (
                      <li key={`r-${idx}`} className="flight-payment-leg">
                        <span className="flight-payment-leg__codes">
                          {row.origin} → {row.dest}
                        </span>
                        <span className="flight-payment-leg__meta">
                          {fmtShortTime(row.dep) ? `${fmtShortTime(row.dep)} · ` : ''}
                          {row.stops === 0 ? 'Direct' : `${row.stops} stop(s)`}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="flight-payment-total">
                    <span className="flight-payment-total__label">Amount due</span>
                    <div className="flight-payment-total__row">
                      <span className="flight-payment-total__formatted">
                        {formatFrom(offer.total_amount, offer.total_currency)}
                      </span>
                    </div>
                    {normalizeCurrencyCode(offer.total_currency || 'GBP') !== selectedCurrency ? (
                      <p className="flight-payment-fx-note">
                        Card charged by Stripe in {String(offer.total_currency || '').toUpperCase()}{' '}
                        ({offer.total_amount}) — same fare as Duffel.
                      </p>
                    ) : null}
                  </div>
                </div>
              </section>

              {payError ? <div className="flight-payment-error">{payError}</div> : null}

              <div className="flight-payment-actions">
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  disabled={payLoading}
                  onClick={handlePay}
                >
                  {payLoading ? 'Opening checkout…' : 'Pay with card'}
                </Button>
                <p className="flight-payment-note">
                  Secured by Stripe. You’ll return here briefly after paying to finalize your booking.
                </p>
              </div>
            </>
          ) : null}

          {!loadError && !offer && resumeKey && offerId ? (
            <p className="flight-payment-loading">Loading fare…</p>
          ) : null}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FlightPayment;
